/**
 * Configurações > Etapas de exame (#811, clínica).
 *
 * ── A lista de onde cada pedido escolhe ──────────────────────────────────
 *
 * A clínica mantém aqui as etapas que um pedido de exame costuma ter, e a
 * ordem delas. Um pedido novo já começa com as ativas, nesta ordem, e quem
 * cadastra tira, move ou escreve outras só para aquele pedido. Mudar a ordem
 * aqui não mexe nos pedidos que já existem.
 *
 * ── Desativar, e não apagar ──────────────────────────────────────────────
 *
 * Etapa desativada sai das opções de pedido novo e continua nos pedidos que
 * já a usam, como as operadoras. Ela vai para a seção "Desativadas", embaixo,
 * e não entra na ordem: só as ativas se ordenam.
 *
 * ── A linha (16/10/2026) ─────────────────────────────────────────────────
 *
 * Eram quatro botões de texto por etapa ("Subir", "Descer", "Editar",
 * "Desativar"). Viraram as duas setas e o menu da etapa; mudar o nome
 * acontece na própria linha. Ao lado, a prévia de como um pedido novo começa.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { EtapaDaClinica } from '@cliqcrm/contracts';
import { useApi, useEtapasDaClinica } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Campo, Icone, MenuAcoes } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import './etapas-de-exame.css';

/** A lista com o item trocado de lugar com o vizinho; na ponta, a mesma lista. */
function mover<T>(lista: readonly T[], indice: number, direcao: -1 | 1): T[] {
  const destino = indice + direcao;
  const nova = [...lista];
  if (destino < 0 || destino >= lista.length) return nova;
  [nova[indice], nova[destino]] = [nova[destino]!, nova[indice]!];
  return nova;
}

export function SecaoEtapasDeExame(): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const etapas = useEtapasDaClinica(true);
  const invalidar = () => void cache.invalidateQueries({ queryKey: chaves.etapasDaClinica() });

  const [nome, definirNome] = useState('');
  const criar = useMutation({
    mutationFn: () => api!.criarEtapaDaClinica({ corpo: { nome: nome.trim() } }),
    onSuccess: () => {
      definirNome('');
      invalidar();
    },
  });
  const ordenar = useMutation({
    mutationFn: (ids: string[]) => api!.ordenarEtapasDaClinica({ corpo: { ids } }),
    onSuccess: invalidar,
  });

  const lista = etapas.data?.itens ?? [];
  const ativas = lista.filter((etapa) => etapa.ativa);
  const desativadas = lista.filter((etapa) => !etapa.ativa);
  // As desativadas vão no fim do pedido de ordem: só as ativas mudam de lugar.
  const reordenar = (indice: number, direcao: -1 | 1) =>
    ordenar.mutate([...mover(ativas, indice, direcao), ...desativadas].map((e) => e.id));

  return (
    <div className="etapas-exame">
      <div className="etapas-exame__principal">
        {etapas.isPending && <p className="etapas-exame__nota">Buscando as etapas…</p>}
        {etapas.isError && <Faixa tom="bloqueado">Não foi possível buscar as etapas.</Faixa>}
        {ordenar.isError && (
          <Faixa tom="bloqueado">
            {recadoDeErro(ordenar.error, { generica: 'Não foi possível mudar a ordem.' })}
          </Faixa>
        )}

        <section className="etapas-exame__cartao" aria-label="Etapas ativas">
          {etapas.isSuccess && ativas.length === 0 && (
            <p className="etapas-exame__nota">
              Nenhuma etapa ativa. Enquanto a lista estiver vazia, cada pedido escreve as suas.
            </p>
          )}
          {ativas.length > 0 && (
            <ol className="etapas-exame__lista" aria-label="Etapas de exame">
              {ativas.map((etapa, indice) => (
                <LinhaDaEtapa
                  key={etapa.id}
                  etapa={etapa}
                  posicao={indice + 1}
                  primeira={indice === 0}
                  ultima={indice === ativas.length - 1}
                  ocupado={ordenar.isPending}
                  aoMover={(direcao) => reordenar(indice, direcao)}
                  aoMudar={invalidar}
                />
              ))}
            </ol>
          )}

          <form
            className="etapas-exame__nova"
            onSubmit={(evento: FormEvent) => {
              evento.preventDefault();
              if (nome.trim() !== '') criar.mutate();
            }}
          >
            <Campo
              id="etapa-de-exame-nome"
              rotulo="Nova etapa"
              type="text"
              value={nome}
              maxLength={80}
              ajuda="Entra no fim da lista. Por exemplo: Guia autorizada, Coleta, Resultado recebido."
              onChange={(e) => definirNome(e.currentTarget.value)}
            />
            <Botao
              type="submit"
              desabilitado={nome.trim() === '' || criar.isPending || api === null}
              motivo={criar.isPending ? 'Acrescentando…' : 'Escreva o nome antes.'}
            >
              {criar.isPending ? 'Acrescentando…' : 'Acrescentar'}
            </Botao>
          </form>
          {criar.isError && (
            <Faixa tom="bloqueado">
              {recadoDeErro(criar.error, { generica: 'Não foi possível cadastrar a etapa.' })}
            </Faixa>
          )}
        </section>

        {desativadas.length > 0 && (
          <section className="etapas-exame__desativadas" aria-labelledby="etapas-desativadas">
            <h3 id="etapas-desativadas" className="etapas-exame__versal">
              Desativadas · {desativadas.length}
            </h3>
            <ul className="etapas-exame__lista">
              {desativadas.map((etapa) => (
                <EtapaDesativada key={etapa.id} etapa={etapa} aoMudar={invalidar} />
              ))}
            </ul>
          </section>
        )}
      </div>

      <aside className="etapas-exame__lado">
        <section className="etapas-exame__cartao" aria-labelledby="etapas-previa">
          <h3 id="etapas-previa" className="etapas-exame__titulo">
            Como um pedido novo começa
          </h3>
          {ativas.length === 0 ? (
            <p className="etapas-exame__nota">Sem etapas: quem cadastra escreve as do pedido.</p>
          ) : (
            <ol className="etapas-exame__previa">
              {ativas.map((etapa) => (
                <li key={etapa.id}>{etapa.nome}</li>
              ))}
            </ol>
          )}
          <p className="etapas-exame__nota">
            Quem cadastra o pedido pode tirar, mover ou escrever outras só para ele. Mudar a ordem
            aqui não mexe nos pedidos que já existem.
          </p>
        </section>
        <p className="admin__nota admin__nota--info">
          Só o administrador mexe nesta lista. Quem atende vê as etapas ao montar um pedido.
        </p>
      </aside>
    </div>
  );
}

function LinhaDaEtapa({
  etapa,
  posicao,
  primeira,
  ultima,
  ocupado,
  aoMover,
  aoMudar,
}: {
  etapa: EtapaDaClinica;
  posicao: number;
  primeira: boolean;
  ultima: boolean;
  ocupado: boolean;
  aoMover: (direcao: -1 | 1) => void;
  aoMudar: () => void;
}): ReactNode {
  const api = useApi();
  const [editando, definirEditando] = useState(false);
  const [nome, definirNome] = useState(etapa.nome);

  const editar = useMutation({
    mutationFn: (mudanca: { nome: string; ativa: boolean }) =>
      api!.editarEtapaDaClinica({ caminho: { id: etapa.id }, corpo: mudanca }),
    onSuccess: () => {
      definirEditando(false);
      aoMudar();
    },
  });

  return (
    <li className="etapas-exame__linha" data-editando={editando || undefined}>
      <span className="etapas-exame__disco" aria-hidden="true">
        {posicao}
      </span>
      {editando ? (
        <form
          className="etapas-exame__edicao"
          onSubmit={(evento) => {
            evento.preventDefault();
            if (nome.trim() !== '') editar.mutate({ nome: nome.trim(), ativa: etapa.ativa });
          }}
        >
          <Campo
            rotulo={`Nome de "${etapa.nome}"`}
            type="text"
            value={nome}
            maxLength={80}
            autoFocus
            onChange={(e) => definirNome(e.currentTarget.value)}
          />
          <span className="etapas-exame__comandos">
            <Botao variante="fantasma" onClick={() => definirEditando(false)}>
              Cancelar
            </Botao>
            <Botao
              type="submit"
              desabilitado={nome.trim() === '' || editar.isPending}
              motivo="Salvando…"
            >
              Salvar nome
            </Botao>
          </span>
        </form>
      ) : (
        <>
          <span className="etapas-exame__nome">{etapa.nome}</span>
          <span className="etapas-exame__comandos">
            <Botao
              miudo
              icone
              variante="fantasma"
              rotulo={`Subir ${etapa.nome}`}
              desabilitado={primeira || ocupado}
              motivo={primeira ? 'Já é a primeira.' : 'Salvando…'}
              onClick={() => aoMover(-1)}
            >
              <span className="etapas-exame__sobe">
                <Icone nome="abaixo" />
              </span>
            </Botao>
            <Botao
              miudo
              icone
              variante="fantasma"
              rotulo={`Descer ${etapa.nome}`}
              desabilitado={ultima || ocupado}
              motivo={ultima ? 'Já é a última.' : 'Salvando…'}
              onClick={() => aoMover(1)}
            >
              <Icone nome="abaixo" />
            </Botao>
            <MenuAcoes
              rotulo={`Ações de ${etapa.nome}`}
              itens={[
                {
                  rotulo: 'Mudar o nome',
                  aoClicar: () => {
                    definirNome(etapa.nome);
                    definirEditando(true);
                  },
                },
                {
                  rotulo: 'Desativar',
                  separadorAntes: true,
                  desabilitado: editar.isPending,
                  motivo: 'Salvando…',
                  aoClicar: () => editar.mutate({ nome: etapa.nome, ativa: false }),
                },
              ]}
            />
          </span>
        </>
      )}
      {editar.isError && (
        <span className="etapas-exame__erro">
          <Faixa tom="bloqueado">
            {recadoDeErro(editar.error, { generica: 'Não foi possível salvar a etapa.' })}
          </Faixa>
        </span>
      )}
    </li>
  );
}

function EtapaDesativada({
  etapa,
  aoMudar,
}: {
  etapa: EtapaDaClinica;
  aoMudar: () => void;
}): ReactNode {
  const api = useApi();
  const reativar = useMutation({
    mutationFn: () =>
      api!.editarEtapaDaClinica({
        caminho: { id: etapa.id },
        corpo: { nome: etapa.nome, ativa: true },
      }),
    onSuccess: aoMudar,
  });
  return (
    <li className="etapas-exame__desativada">
      <span className="etapas-exame__nome">{etapa.nome}</span>
      <span className="etapas-exame__apoio">
        fora dos pedidos novos; os que já a usam continuam iguais
      </span>
      <span className="etapas-exame__comandos">
        <Botao
          miudo
          variante="secundario"
          aria-label={`Reativar ${etapa.nome}`}
          desabilitado={reativar.isPending}
          motivo="Salvando…"
          onClick={() => reativar.mutate()}
        >
          Reativar
        </Botao>
      </span>
      {reativar.isError && (
        <span className="etapas-exame__erro">
          <Faixa tom="bloqueado">
            {recadoDeErro(reativar.error, { generica: 'Não foi possível reativar a etapa.' })}
          </Faixa>
        </span>
      )}
    </li>
  );
}
